import type {
  ReportLinkedDataIssue,
  ReportLinkedDataStatus,
} from 'features/reporting/types'

export interface RugLinkedDataInspection {
  id: string
  label: string
  componentId?: string
  jobId?: string
}

export interface RugLinkedDataForm {
  inspectionId?: string
}

export interface RugLinkedDataJob {
  id: string
  salesforceJobId?: string
}

export interface RugLinkedDataInput {
  inspections: readonly RugLinkedDataInspection[]
  inspectionForms: readonly RugLinkedDataForm[]
  jobs: readonly RugLinkedDataJob[]
  isLoading: boolean
  hasError: boolean
}

const getTrimmedValue = (value: string | undefined): string | undefined => {
  const trimmedValue = value?.trim()
  return trimmedValue ? trimmedValue : undefined
}

const getMissingSources = ({
  inspection,
  inspectionIdsWithForms,
  jobsById,
}: {
  inspection: RugLinkedDataInspection
  inspectionIdsWithForms: ReadonlySet<string>
  jobsById: ReadonlyMap<string, RugLinkedDataJob>
}): ReportLinkedDataIssue['missingSources'] => {
  const missingSources: ReportLinkedDataIssue['missingSources'] = []

  if (!inspectionIdsWithForms.has(inspection.id)) {
    missingSources.push('inspection-form')
  }

  const jobId = getTrimmedValue(inspection.jobId)
  const salesforceJobId = jobId
    ? getTrimmedValue(jobsById.get(jobId)?.salesforceJobId)
    : undefined
  if (!salesforceJobId) {
    missingSources.push('sf-job')
  }

  return missingSources
}

export const getRugLinkedDataStatus = ({
  inspections,
  inspectionForms,
  jobs,
  isLoading,
  hasError,
}: RugLinkedDataInput): ReportLinkedDataStatus => {
  if (isLoading || hasError) {
    return { issues: [], isLoading, hasError }
  }

  const inspectionIdsWithForms = new Set(
    inspectionForms
      .map(({ inspectionId }) => getTrimmedValue(inspectionId))
      .filter((inspectionId): inspectionId is string => Boolean(inspectionId))
  )
  const jobsById = new Map(jobs.map((job) => [job.id, job]))
  const issues = inspections.reduce<ReportLinkedDataIssue[]>(
    (currentIssues, inspection) => {
      const missingSources = getMissingSources({
        inspection,
        inspectionIdsWithForms,
        jobsById,
      })
      if (missingSources.length === 0) return currentIssues

      currentIssues.push({
        inspectionId: inspection.id,
        inspectionLabel: inspection.label,
        componentId: inspection.componentId,
        jobId: inspection.jobId,
        missingSources,
      })
      return currentIssues
    },
    []
  )

  return { issues, isLoading: false, hasError: false }
}
